import { useEffect, useState } from "react";
import { Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, C, errorMessage, fmtChips } from "../../lib/api";
import { avatarSrc } from "../../lib/avatars";
import { Field, fmtNum, fmtWhen, inputCls } from "./ui";

const route = (r, id) => r.replace("{user_id}", id);

// Slide-in player detail: balance, table, ledger (append-only), chip adjustment with reason, block/unblock, staff role.
// Time bank cards: inventory, cards at a seat, and every movement (bought / to table / back / played / staff fix) — for "I'm missing some" claims.
const TB_LABEL = { PURCHASE: "bought", TO_TABLE: "taken to table", FROM_TABLE: "returned from table", USED: "played at table", ADJUSTMENT: "staff adjustment" };
const TimebankSection = ({ tb, userId, run, busy }) => {
  const [fix, setFix] = useState({ cards: "", reason: "" });
  const adjust = (sign) => run(() => api.post(route(C.ApiRoutes.ADMIN_PLAYER_TIMEBANK, userId), { cards: sign * Math.abs(Number(fix.cards)), reason: fix.reason }).then(() => setFix({ cards: "", reason: "" })), "Time bank cards adjusted");
  return (
    <section data-testid="pd-timebank">
      <div className="text-[11px] uppercase tracking-[0.25em] gold-text mb-3">Time bank cards</div>
      <div className="flex items-center gap-6 text-sm mb-3">
        <div><span className="text-slate-400 text-xs">In account </span><span className="font-mono text-cyan-200" data-testid="pd-tb-inventory">{tb.inventory}</span></div>
        <div><span className="text-slate-400 text-xs">At seat </span><span className="font-mono text-cyan-200" data-testid="pd-tb-seat">{tb.at_seat}</span></div>
      </div>
      <div className="grid grid-cols-5 gap-2 items-end mb-3">
        <Field label="Cards"><input data-testid="pd-tb-cards" type="number" min="1" className={inputCls} value={fix.cards} onChange={(e) => setFix({ ...fix, cards: e.target.value })} /></Field>
        <div className="col-span-2"><Field label="Reason (recorded)"><input data-testid="pd-tb-reason" className={inputCls} value={fix.reason} onChange={(e) => setFix({ ...fix, reason: e.target.value })} placeholder="Verified missing cards…" maxLength={200} /></Field></div>
        <button data-testid="pd-tb-add" disabled={busy || !fix.cards || fix.reason.length < 3} onClick={() => adjust(1)} className="rounded-full py-2 text-xs bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30 disabled:opacity-40">Add</button>
        <button data-testid="pd-tb-remove" disabled={busy || !fix.cards || fix.reason.length < 3} onClick={() => adjust(-1)} className="rounded-full py-2 text-xs bg-red-500/20 text-red-200 hover:bg-red-500/30 disabled:opacity-40">Remove</button>
      </div>
      <div className="flex flex-col divide-y divide-white/5 text-xs max-h-56 overflow-y-auto" data-testid="pd-tb-ledger">
        {tb.ledger.length === 0 && <div className="text-slate-500 py-2">No time bank activity yet.</div>}
        {tb.ledger.map((e, i) => (
          <div key={i} className="py-2 flex items-center justify-between gap-3">
            <div>
              <div className="text-slate-200">{TB_LABEL[e.kind] || e.kind}{e.reason ? ` — ${e.reason}` : ""}{e.why ? ` (${e.why.replace(/_/g, " ")})` : ""}</div>
              <div className="text-[10px] text-slate-500">{fmtWhen(e.at)}{e.table_id ? ` · ${e.table_id}` : ""}{e.hand_id ? ` · hand ${e.hand_id.slice(0, 8)}` : ""}{e.kind === "USED" ? ` · ${e.seat_cards_left} left at seat` : ""} · {e.inventory_after} in account</div>
            </div>
            <div className={`font-mono ${e.delta > 0 ? "text-emerald-300" : e.delta < 0 ? "text-red-300" : "text-slate-400"}`}>{e.delta > 0 ? "+" : ""}{e.delta === 0 ? "−1 seat" : e.delta}</div>
          </div>
        ))}
      </div>
    </section>
  );
};

const EVENT_LABEL = { SIGN_IN: "Signed in", PASSWORD_CHANGED: "Password changed", RESET_LINK_ISSUED: "Reset link issued by staff" };

// What has happened to this account: every sign-in with the address and ISP it came from, and password changes.
const SecuritySection = ({ userId }) => {
  const [events, setEvents] = useState(null);
  useEffect(() => { api.get(route(C.ApiRoutes.ADMIN_PLAYER_SECURITY, userId)).then((r) => setEvents(r.data.events)).catch(() => setEvents([])); }, [userId]);
  return (
    <section data-testid="pd-security">
      <div className="text-[11px] uppercase tracking-[0.25em] gold-text mb-3">Account activity</div>
      <div className="flex flex-col divide-y divide-white/5 text-xs max-h-56 overflow-y-auto">
        {events === null && <div className="text-slate-500 py-2">Loading…</div>}
        {events?.length === 0 && <div className="text-slate-500 py-2">Nothing recorded yet — sign-ins and password changes will appear here.</div>}
        {(events || []).map((e, i) => (
          <div key={i} data-testid={`pd-security-row-${i}`} className="py-2 flex items-start justify-between gap-3">
            <div>
              <div className="text-slate-200">
                {EVENT_LABEL[e.event] || e.event}
                {e.new_device && e.event === "SIGN_IN" && <span className="ml-2 text-[9px] uppercase tracking-wider rounded-full px-1.5 py-0.5 bg-amber-400/20 text-amber-200">new device</span>}
                {e.by && <span className="text-slate-500"> · by {e.by}</span>}
              </div>
              <div className="text-[10px] text-slate-500">{fmtWhen(e.at)}{e.note ? ` · ${e.note}` : ""}</div>
            </div>
            <div className="text-right text-[10px] text-slate-400 shrink-0">
              <div className="font-mono">{e.ip || "—"}{e.country ? ` · ${e.country}` : ""}</div>
              <div title={e.org || ""}>{e.isp || "ISP unknown"}{e.proxy ? " · proxy" : ""}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export const PlayerDrawer = ({ userId, isAdmin, onClose, onChange }) => {
  const [p, setP] = useState(null);
  const [adj, setAdj] = useState({ amount: "", reason: "" });
  const [blockReason, setBlockReason] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get(route(C.ApiRoutes.ADMIN_PLAYER, userId)).then((r) => setP(r.data)).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => { load(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async (fn, ok, closeAfter = false) => {
    setBusy(true);
    try {
      await fn();
      toast.success(ok);
      onChange();
      if (closeAfter) return onClose();  // the player is gone: nothing left to reload
      await load();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const adjust = (sign) => run(() => api.post(route(C.ApiRoutes.ADMIN_PLAYER_ADJUST, userId), { amount: sign * Math.abs(Number(adj.amount)), reason: adj.reason }).then(() => setAdj({ amount: "", reason: "" })), "Chips adjusted");
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60" onClick={onClose} data-testid="player-drawer">
      <aside onClick={(e) => e.stopPropagation()} className="w-full max-w-xl h-full overflow-y-auto gold-card border-l border-amber-400/30 p-6 flex flex-col gap-6" style={{ animation: "rise 300ms both" }}>
        <div className="flex items-start justify-between">
          {p ? (
            <div className="flex items-center gap-4">
              <img src={avatarSrc(p.avatar)} alt="" className="w-16 h-16 rounded-full bg-slate-800 border-2 border-amber-400/50" />
              <div>
                <div className="text-xl font-display" data-testid="pd-name">{p.display_name} {p.role !== "player" && <span className="text-[10px] uppercase tracking-wider text-amber-300 ml-1">{p.role}</span>}</div>
                <div className="text-xs text-slate-400">{p.email} · {p.country || "—"} · <span className="font-mono">{p.user_id}</span></div>
                <div className="text-xs text-slate-400">joined {fmtWhen(p.created_at)} · last seen {fmtWhen(p.last_login_at)}</div>
              </div>
            </div>
          ) : <div className="text-slate-400 text-sm">Loading…</div>}
          <button data-testid="pd-close" onClick={onClose} className="p-2 rounded-full hover:bg-white/10"><X className="w-5 h-5" /></button>
        </div>
        {p && (<>
          <div className="grid grid-cols-3 gap-3">
            <div className="glass rounded-xl px-4 py-3"><div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Chips</div><div className="font-mono text-2xl text-amber-100" data-testid="pd-balance">{fmtChips(p.balance)}</div></div>
            <div className="glass rounded-xl px-4 py-3"><div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Hands</div><div className="font-mono text-2xl">{fmtNum(p.hands)}</div></div>
            <div className="glass rounded-xl px-4 py-3"><div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Net</div><div className={`font-mono text-2xl ${p.net >= 0 ? "text-emerald-300" : "text-red-300"}`}>{p.net >= 0 ? "+" : ""}{fmtNum(p.net)}</div></div>
          </div>
          {p.seated && <div className="text-xs text-amber-200" data-testid="pd-seated">Seated at {p.seated.name} with {fmtChips(p.seated.stack)}</div>}
          {p.blocked && <div className="rounded-xl bg-red-500/15 border border-red-400/40 px-4 py-3 text-sm text-red-200" data-testid="pd-blocked">Blocked{p.block_reason ? ` — ${p.block_reason}` : ""}</div>}

          <section>
            <div className="text-[11px] uppercase tracking-[0.25em] gold-text mb-3">Adjust chips</div>
            <div className="grid grid-cols-3 gap-3 items-end">
              <Field label="Amount"><input data-testid="pd-adj-amount" type="number" min={1} className={inputCls} value={adj.amount} onChange={(e) => setAdj({ ...adj, amount: e.target.value })} placeholder="1000" /></Field>
              <Field label="Reason (recorded in the ledger)"><input data-testid="pd-adj-reason" className={inputCls} value={adj.reason} onChange={(e) => setAdj({ ...adj, reason: e.target.value })} placeholder="Promo / correction…" maxLength={200} /></Field>
              <div className="flex gap-2">
                <button data-testid="pd-adj-add" disabled={busy || !Number(adj.amount) || adj.reason.trim().length < 3} onClick={() => adjust(1)} className="flex-1 rounded-md py-2 text-xs font-display bg-emerald-500/80 text-slate-900 btn-lift">+ Add</button>
                <button data-testid="pd-adj-remove" disabled={busy || !Number(adj.amount) || adj.reason.trim().length < 3} onClick={() => adjust(-1)} className="flex-1 rounded-md py-2 text-xs font-display bg-red-500/80 text-white btn-lift">− Remove</button>
              </div>
            </div>
          </section>

          <SecuritySection userId={userId} />

          <section>
            <div className="text-[11px] uppercase tracking-[0.25em] gold-text mb-3">Password</div>
            <button data-testid="pd-reset-link" disabled={busy} title="A one-time link this player can use to set a new password — hand it over yourself while the room's email is off"
              onClick={async () => { try { const { data } = await api.post(route(C.ApiRoutes.ADMIN_PLAYER_RESET_LINK, userId)); await navigator.clipboard?.writeText(data.link); toast.success("Reset link copied", { description: `Works once, expires in ${data.expires_minutes} minutes.` }); } catch (e) { toast.error(errorMessage(e)); } }}
              className="rounded-full px-4 py-2 text-xs bg-white/5 border border-white/10 btn-lift">Copy password-reset link</button>
          </section>

          <section className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] gold-text mb-3">Access</div>
              {p.blocked ? (
                <button data-testid="pd-unblock" disabled={busy} onClick={() => run(() => api.post(route(C.ApiRoutes.ADMIN_PLAYER_BLOCK, userId), { blocked: false }), "Player unblocked")} className="rounded-full px-4 py-2 text-xs bg-emerald-500/20 text-emerald-200 border border-emerald-400/40 btn-lift">Unblock</button>
              ) : p.role === "admin" ? <div className="text-xs text-slate-500">Admins can't be blocked.</div> : (
                <div className="flex flex-col gap-2">
                  <input data-testid="pd-block-reason" className={inputCls} value={blockReason} onChange={(e) => setBlockReason(e.target.value)} placeholder="Reason (optional)" maxLength={200} />
                  <button data-testid="pd-block" disabled={busy} onClick={() => { if (window.confirm(`Block ${p.display_name}? They will be removed from any table and can't sign in.`)) run(() => api.post(route(C.ApiRoutes.ADMIN_PLAYER_BLOCK, userId), { blocked: true, reason: blockReason }), "Player blocked"); }} className="rounded-full px-4 py-2 text-xs bg-red-500/20 text-red-200 border border-red-400/40 btn-lift">Block player</button>
                </div>
              )}
            </div>
            {isAdmin && p.role !== "admin" && (
              <div>
                <div className="text-[11px] uppercase tracking-[0.25em] gold-text mb-3">Role</div>
                <button data-testid="pd-role" disabled={busy} onClick={() => run(() => api.post(route(C.ApiRoutes.ADMIN_PLAYER_ROLE, userId), { role: p.role === "staff" ? "player" : "staff" }), "Role updated")} className="rounded-full px-4 py-2 text-xs bg-white/5 border border-white/10 btn-lift">
                  {p.role === "staff" ? "Remove staff access" : "Make staff"}
                </button>
              </div>
            )}
          </section>

          {isAdmin && p.role !== "admin" && !p.house && (
            <section data-testid="pd-danger" className="rounded-xl border border-red-400/30 bg-red-500/5 px-4 py-3 flex flex-col gap-2">
              <div className="text-[11px] uppercase tracking-[0.25em] text-red-300">Delete account</div>
              <p className="text-xs text-slate-400 leading-relaxed">
                Permanent. The account, its chips and its sign-in are gone and cannot be restored. Purchases, ledger
                entries, hands and rake stay on file for your records. To stop someone playing without losing the
                account, use Block above — that one is reversible.
              </p>
              <button data-testid="pd-delete" disabled={busy}
                onClick={() => {
                  const typed = window.prompt(`Delete ${p.display_name} for good?\n\nType their screen name to confirm:`);
                  if (typed == null) return;
                  if (typed.trim().toLowerCase() !== (p.display_name || "").toLowerCase()) { toast.error("Name didn't match — nothing was deleted"); return; }
                  run(() => api.delete(route(C.ApiRoutes.ADMIN_PLAYER, userId)), `${p.display_name} deleted`, true);
                }}
                className="self-start rounded-full px-4 py-2 text-xs bg-red-600/80 text-white border border-red-300/40 btn-lift flex items-center gap-1.5">
                <Trash2 className="w-3.5 h-3.5" /> Delete permanently
              </button>
            </section>
          )}

          {p.timebank && <TimebankSection tb={p.timebank} userId={userId} run={run} busy={busy} />}

          <section>
            <div className="text-[11px] uppercase tracking-[0.25em] gold-text mb-3">Ledger · latest {p.ledger.length}</div>
            <div className="flex flex-col divide-y divide-white/5 text-xs" data-testid="pd-ledger">
              {p.ledger.map((e) => (
                <div key={e.entry_id} className="py-2 flex items-center justify-between gap-3">
                  <div><div className="text-slate-200">{e.type.replace(/_/g, " ").toLowerCase()}{e.meta?.reason ? ` — ${e.meta.reason}` : ""}</div><div className="text-[10px] text-slate-500">{fmtWhen(e.created_at)}{e.ref ? ` · ${e.ref}` : ""}</div></div>
                  <div className={`font-mono ${e.amount >= 0 ? "text-emerald-300" : "text-red-300"}`}>{e.amount >= 0 ? "+" : ""}{fmtNum(e.amount)}</div>
                </div>
              ))}
            </div>
          </section>
        </>)}
      </aside>
    </div>
  );
};
